import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { SITE } from "@/data/site";
import { scrollToId } from "./useCopy";

const LINES = [
  { text: "Your boss", cls: "text-slate-50" },
  { text: "has a", cls: "text-outline italic" },
  { text: "boss.", cls: "text-gold" },
];
const ease = [0.16, 1, 0.3, 1];

const Portrait = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 18 });
  const { scrollY } = useScroll();
  const py = useTransform(scrollY, [0, 800], [0, 120]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <motion.div style={{ y: py }} className="relative mx-auto w-full max-w-[520px] [perspective:1200px]">
      <div className="glow-pulse absolute -inset-10 rounded-full bg-[radial-gradient(circle,rgba(20,184,166,0.45),transparent_65%)] blur-2xl" />
      <div className="spin-slow absolute -right-6 -top-6 z-20 h-28 w-28 sm:h-32 sm:w-32">
        <svg viewBox="0 0 100 100" className="h-full w-full">
          <defs><path id="circ" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs>
          <text className="font-mono" fontSize="9.5" letterSpacing="3" fill="#fbbf24">
            <textPath href="#circ">CHIEF EXECUTIVE OVERLORD • EST. TODAY •</textPath>
          </text>
        </svg>
        <span className="absolute inset-0 grid place-items-center font-display text-xl font-black text-amber-300">$</span>
      </div>
      <motion.div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={reset}
        initial={{ opacity: 0, scale: 1.08, clipPath: "inset(100% -30% -30% -30%)" }}
        animate={{ opacity: 1, scale: 1, clipPath: "inset(0% -30% -30% -30%)" }}
        transition={{ duration: 1.4, delay: 0.5, ease }}
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        className="relative"
        data-testid="hero-portrait"
      >
        <div className="clip-frame relative bg-gradient-to-br from-amber-300 via-amber-500/60 to-teal-400 p-[2px]">
          <div className="clip-frame relative overflow-hidden bg-[#07090e]">
            <img src="/img/overlord.jpg" alt="The Corporate Overlord, a crowned CEO behind his desk of gold coins" className="aspect-square w-full object-cover" />
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,transparent_40%,rgba(7,9,14,0.7)_100%)]" />
          </div>
        </div>
        <div style={{ transform: "translateZ(60px)" }} className="clip-chip absolute -bottom-5 left-6 border border-amber-400/40 bg-[#0e131f]/90 px-4 py-2 backdrop-blur-md">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-teal-300">Approval rating</p>
          <p className="font-display text-xl font-bold text-gold">100% (mandatory)</p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => (
  <section id="top" className="relative min-h-screen overflow-hidden pt-28 sm:pt-32" data-testid="hero-section">
    <div className="grid-lines absolute inset-0 [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
    <div className="absolute left-[-10%] top-[20%] h-[420px] w-[420px] rounded-full bg-amber-500/10 blur-[120px]" />
    <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-5 pb-44 sm:px-8 lg:pb-28 lg:grid-cols-[1.15fr_1fr] lg:gap-10">
      <div className="min-w-0">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 1 }}
          className="mb-8 font-mono text-xs uppercase tracking-[0.35em] text-teal-300">
          Memo #001 — From the desk of the CEO
        </motion.p>
        <h1 className="font-display text-[15vw] font-black uppercase leading-[0.88] tracking-tight sm:text-7xl lg:text-[7.5rem]" data-testid="hero-title">
          {LINES.map((l, i) => (
            <span key={l.text} className="block overflow-hidden pb-2">
              <motion.span
                className={`block ${l.cls}`}
                initial={{ y: "110%", rotate: 4 }}
                animate={{ y: "0%", rotate: 0 }}
                transition={{ duration: 1.1, delay: 0.35 + i * 0.14, ease }}
              >
                {l.text}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.9, ease }}>
          <p className="mt-8 max-w-lg text-base leading-relaxed text-slate-300 sm:text-lg">
            Meet the Corporate Overlord — the CEO who runs the entire corporate world. Money, power, endless meetings
            and questionable business decisions. He answers to nobody… <span className="text-amber-300">or so he thinks.</span>
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button onClick={() => scrollToId("how-to-buy")} data-testid="hero-buy-button"
              className="btn-gold clip-chip px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.2em]">
              Buy {SITE.ticker}
            </button>
            <button onClick={() => scrollToId("lore")} data-testid="hero-lore-button"
              className="group flex items-center gap-2 border-b border-teal-400/50 pb-1 font-mono text-sm uppercase tracking-[0.2em] text-teal-200 transition-colors hover:text-amber-300">
              Read the dossier <ArrowDownRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:translate-y-1" />
            </button>
          </div>
        </motion.div>
      </div>
      <Portrait />
    </div>
  </section>
);
